import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, Phone } from "lucide-react";
import type { AvailabilityStatus } from "../../api/callsAPI";
import { useCallCenter } from "./CallCenterContext";

const OPTIONS: Array<{ value: AvailabilityStatus; label: string; hint: string; dot: string }> = [
  { value: "available", label: "Available", hint: "Ring me for calls", dot: "bg-green-500" },
  { value: "busy", label: "Busy", hint: "Don't ring me for now", dot: "bg-amber-500" },
  { value: "off", label: "Off", hint: "I'm not taking calls", dot: "bg-gray-400" },
];

/**
 * Available / Busy / Off for receiving routed calls, in the sidebar header.
 * Renders nothing for anyone who cannot take calls. Warns when the person is
 * "Available" but this browser is not actually able to ring.
 */
const CallStatusToggle: React.FC<{ className?: string }> = ({ className = "" }) => {
  const cc = useCallCenter();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!cc.active) return null;

  const current = OPTIONS.find((o) => o.value === cc.status) || OPTIONS[2];

  // Only the tab holding the device knows whether it is registered.
  let warning: string | null = null;
  if (cc.isLeader && cc.status === "available") {
    if (!cc.alertsEnabled) warning = "Call alerts are off in this browser, so it won't ring.";
    else if (cc.deviceError) warning = cc.deviceError;
    else if (!cc.registered) warning = "This browser isn't connected for calls yet, so it won't ring.";
  }

  const choose = async (value: AvailabilityStatus) => {
    setOpen(false);
    if (value !== cc.status) await cc.setStatus(value);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Call status: ${current.label}${warning ? ". " + warning : ""}`}
        disabled={cc.statusSaving}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-white hover:bg-white hover:bg-opacity-20 disabled:opacity-60"
      >
        <Phone className="w-4 h-4" aria-hidden="true" />
        <span className={`inline-block h-2 w-2 rounded-full ${current.dot}`} aria-hidden="true" />
        <span>{current.label}</span>
        {warning && <AlertTriangle className="w-3.5 h-3.5 text-amber-300" aria-hidden="true" />}
        <ChevronDown className="w-3 h-3" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Call status"
          className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-[60] w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 text-sm"
        >
          {OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={o.value === cc.status}
              aria-label={`${o.label}: ${o.hint}`}
              onClick={() => choose(o.value)}
              className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <span className={`mt-1.5 inline-block h-2 w-2 rounded-full ${o.dot}`} aria-hidden="true" />
              <span>
                <span className="block font-medium text-gray-900 dark:text-white">{o.label}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{o.hint}</span>
              </span>
            </button>
          ))}
          {!cc.isLeader && (
            <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
              Calls ring in another tab of this browser.
            </p>
          )}
          {warning && (
            <div className="px-3 py-2 text-xs text-amber-700 dark:text-amber-300 border-t border-gray-100 dark:border-gray-700">
              <p>{warning}</p>
              {!cc.alertsEnabled && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    cc.enableAlerts();
                  }}
                  className="mt-1 font-semibold underline text-[var(--cc-link)]"
                >
                  Turn on call alerts
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CallStatusToggle;
